import { useLiveQuery } from 'dexie-react-hooks';
import { curriculum } from '@/content/curriculum';
import type { LearnerProfile } from '@/domain/records';
import { recommend, type Recommendation } from '@/features/mastery/recommend';
import { db } from '@/services/db/database';

/** Live mastery records of a learner, keyed by objective. */
export function useMastery(learnerId: string) {
  return useLiveQuery(async () => {
    const records = await db.mastery.where('learnerId').equals(learnerId).toArray();
    return new Map(records.map((r) => [r.objectiveId, r]));
  }, [learnerId]);
}

export function useRecommendations(
  learner: LearnerProfile,
  subjectId?: string,
): Recommendation[] | undefined {
  const mastery = useMastery(learner.id);
  if (!mastery) return undefined;
  return recommend({
    curriculum,
    levelId: learner.levelId,
    variantId: learner.variantId,
    mastery,
    now: new Date(),
    ...(subjectId ? { subjectId } : {}),
  });
}

export function useSessions(learnerId: string) {
  return useLiveQuery(
    () =>
      db.sessions
        .where('[learnerId+startedAt]')
        .between([learnerId, ''], [learnerId, '￿'])
        .toArray(),
    [learnerId],
  );
}

export function useAchievements(learnerId: string) {
  return useLiveQuery(
    () => db.achievements.where('learnerId').equals(learnerId).toArray(),
    [learnerId],
  );
}

/** Link to play a game on some objectives. */
export function playPath(
  gameId: string,
  objectiveIds: string[],
  options: { easier?: boolean; review?: boolean } = {},
) {
  const params = new URLSearchParams();
  for (const id of objectiveIds) params.append('objective', id);
  if (options.easier) params.set('easier', '1');
  if (options.review) params.set('review', '1');
  return `/learn/play/${gameId}?${params.toString()}`;
}

export function recommendationPath(rec: Recommendation) {
  return playPath(rec.game, [rec.objectiveId], {
    easier: rec.easier,
    review: rec.kind === 'review',
  });
}
